import React, { useState } from "react";
import clsx from "clsx";
import useTheme from "@material-ui/core/styles/useTheme";
import Accordion from "@material-ui/core/Accordion";
import AccordionSummary from "@material-ui/core/AccordionSummary";
import AccordionDetails from "@material-ui/core/AccordionDetails";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";

import Loader from "components/Loader";
import { MuiTheme } from "theme";
import useStyles from "./styles";

export interface Props {
  count: number;
  className?: string;
  name: string | React.ReactElement;
  subtitle?: string | React.ReactElement;
  children: React.ReactNode;
  alert?: boolean;
  hideShadow?: boolean;
  hideBackground?: boolean;
  roundCount?: boolean;
  rootRef?: any;
  timeout?: number;
  isExpandedByDefault?: boolean;
  hideSubtitleWhenCollapsed?: boolean;
  showCountLoader?: boolean;
  isLoading?: boolean;
  disabled?: boolean;
}

const AccordionCustom = (props: Props) => {
  const {
    className,
    count,
    name,
    subtitle,
    children,
    rootRef,
    alert = false,
    hideBackground,
    hideShadow,
    roundCount,
    timeout = 300,
    isExpandedByDefault = false,
    hideSubtitleWhenCollapsed = false,
    showCountLoader = false,
    isLoading = false,
    disabled = false
  } = props;
  const theme = useTheme<MuiTheme>();
  const classes = useStyles();
  const [expanded, setExpanded] = useState<boolean>(isExpandedByDefault);

  const handleChange = (e: any) => {
    setExpanded(!expanded);

    if (!expanded) {
      const parentNode = e.currentTarget.parentNode;
      setTimeout(() => handleScroll(parentNode), timeout); //timeout = expanding animation time
    }
  };

  const handleScroll = (parentNode: HTMLElement) => {
    const paddingTop = 5;
    const { bottom, height, y } = parentNode.getBoundingClientRect();
    const footerHeight = theme.heightFooter + paddingTop;

    if (rootRef && rootRef.current) {
      const rootRect = rootRef.current.getBoundingClientRect();

      if (bottom < rootRect.bottom) {
        return;
      }

      if (height > window.innerHeight - footerHeight - rootRect.top) {
        rootRef.current.scrollTo(
          0,
          rootRef.current.scrollTop + y - rootRect.top - paddingTop
        );
      } else {
        rootRef.current.scrollTo(
          0,
          rootRef.current.scrollTop + bottom - window.innerHeight + footerHeight
        );
      }
    }
  };

  const renderCount = () => {
    if (showCountLoader) {
      return isLoading ? (
        <Loader size={16} className={classes.loader} />
      ) : (
        count
      );
    }
    return count;
  };

  return (
    <>
      <Accordion
        classes={{
          root: classes.root,
          disabled: classes.disabled
        }}
        className={clsx(
          {
            [classes.hideBg]: hideBackground,
            [classes.hideShadow]: hideShadow,
            [classes.isLoading]: isLoading
          },
          className
        )}
        disabled={disabled}
        expanded={expanded}
        onChange={e => handleChange(e)}
        TransitionProps={{ timeout: timeout }}
      >
        <AccordionSummary
          className={clsx(classes.summary, `${className}__summary`, {
            [classes.hideBorder]: hideBackground
          })}
          classes={{
            expanded: classes.summaryExpanded,
            expandIcon: classes.summaryIcon
          }}
          expandIcon={<ExpandMoreIcon />}
        >
          <div className={clsx(classes.summary, `${className}__summary`)}>
            <div
              className={clsx(
                classes.summaryCount,
                `${className}__summary-count`,
                {
                  [classes.roundSummaryCount]: roundCount,
                  [classes.summaryCountAlert]: alert
                }
              )}
            >
              {renderCount()}
            </div>
            <div className={classes.summaryTitle}>{name}</div>
            {Boolean(subtitle) && (
              <div
                className={clsx(classes.summarySubtitle, {
                  [classes.hideSubtitle]: hideSubtitleWhenCollapsed && !expanded
                })}
              >
                {subtitle}
              </div>
            )}
          </div>
        </AccordionSummary>

        <AccordionDetails classes={{ root: classes.details }}>
          {children}
        </AccordionDetails>
      </Accordion>
    </>
  );
};

export default AccordionCustom;
